import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import urlJoin from 'url-join';
import { urls } from 'app-constants';
import { useUserContext } from 'context';
import { useHistory, useWidgetId } from 'hooks';
import DropdownButton from 'components/DropdownButton';
import Icon from 'components/Icon';
import LoadingBubbles from 'components/LoadingBubbles';
import TruncateText from 'components/TruncateText';
import LoginView from 'components/LoginView';

const UserMenu = ({ initialIsAuthenticated = false, isSocialAuthEnabled = false }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(initialIsAuthenticated);
  const {
    authenticated,
    authRequested,
    setAuthRequested,
    isFetching: userDataFetching,
    firstName,
    username,
    isSuperuser,
    isStaff,
    isEditor,
    isZeditor,
    isProducer,
    producerQuotas = [],
    hasActiveSubscription,
    id: userId,
  } = useUserContext();

  const { history, location } = useHistory();

  useEffect(() => {
    if (location.hash === '#register') {
      if (!isAuthenticated) setAuthRequested({ register: true });
      history.push({ hash: '' });
    }
  }, [location.hash, isAuthenticated]);

  useEffect(() => {
    if (typeof authenticated !== 'undefined') {
      setIsAuthenticated(authenticated);
    }
  }, [authenticated]);

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ userId });
  }, [userId]);

  const labelText = firstName || <TruncateText text={username} numChars={15} />;

  // const loginUrl = `${urls.login}?next=${window.location.pathname}`;
  const logoutUrl = `${urls.logout}?next=${window.location.pathname}`;

  const widgetId = useWidgetId();

  const [loginModalIsOpen, setLoginModalIsOpen] = useState(false);
  const [isRegisterCTA, setIsRegisterCTA] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState(null);
  const handleLoginClick = evt => {
    evt.preventDefault();
    setLoginModalIsOpen(true);
  };
  const handleLoginModalClose = () => {
    setLoginModalIsOpen(false);
    setRedirectUrl(null);
  };

  useEffect(() => {
    if (authRequested) {
      if (typeof authRequested === 'object') {
        setIsRegisterCTA(authRequested.register || false);
        setRedirectUrl(authRequested.redirect);
      }
      setLoginModalIsOpen(true);
      setAuthRequested(null);
    }
  }, [authRequested]);

  useEffect(() => {
    if (isAuthenticated && redirectUrl) {
      window.location.href = redirectUrl;
    }
    if (isAuthenticated && loginModalIsOpen) {
      setLoginModalIsOpen(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!loginModalIsOpen) {
      setIsRegisterCTA(false);
    }
  }, [loginModalIsOpen]);

  const producerAccess = isProducer && hasActiveSubscription;
  let producerSectionLabel = 'Venue Management';
  if (producerQuotas.length === 1) {
    producerSectionLabel = producerQuotas[0].poi.name;
  }

  const links = !userId ? [] : [
    {
      url: urls.account,
      label: 'Account Details',
      icon: 'circle-user',
      access: true,
    },
    {
      url: urls.savedEvents,
      label: 'Saved Events',
      icon: ['far', 'calendar-star'],
      access: true,
    },
    {
      url: urls.savedVenues,
      label: 'Saved Venues',
      icon: ['far', 'building'],
      access: true,
    },
    {
      url: urls.savedSearches,
      label: 'Saved Searches',
      icon: 'search',
      access: true,
    },
    {
      url: urls.savedAddresses,
      label: 'Saved Addresses',
      icon: ['far', 'address-book'],
      access: true,
    },

    // -----------------------------------
    {
      type: 'separator',
      label: producerSectionLabel,
      access: producerAccess,
    },
    {
      url: urls.producerVenueProfile,
      label: 'Venue Profile',
      icon: ['far', 'building'],
      access: producerAccess,
    },
    {
      url: urls.producerEventList,
      label: 'Events',
      icon: ['far', 'calendar'],
      access: producerAccess,
    },
    {
      url: urls.producerStats,
      label: 'Stats',
      icon: 'chart-simple',
      access: producerAccess,
    },
    {
      url: urls.producerCalendars,
      label: 'Calendars',
      icon: ['far', 'calendars'],
      access: producerAccess,
    },
    {
      url: urls.producerAPIReference,
      label: 'API Documentation',
      icon: 'code',
      access: producerAccess,
      targetBlank: true,
    },
    {
      url: urls.producerContactForm,
      label: 'Support',
      icon: ['far', 'circle-info'],
      access: producerAccess,
    },

    // -----------------------------------
    {
      type: 'separator',
      label: 'Editor Tools',
      access: isSuperuser || isZeditor,
    },
    {
      url: urls.addEvent,
      label: 'Add Event',
      icon: ['far', 'calendar-circle-plus'],
      access: isSuperuser || isZeditor,
    },
    {
      url: urls.addPOI,
      label: 'Add POI',
      icon: 'location-plus',
      access: isSuperuser || isZeditor,
    },
    {
      url: urls.poiQAReport,
      label: 'POI QA Report',
      icon: 'location-check',
      access: isZeditor && !isSuperuser,
    },
    {
      url: urls.editorPois(userId),
      label: 'My POIs',
      icon: ['far', 'building'],
      access: isSuperuser || isZeditor,
    },
    {
      url: urls.editorEvents(userId),
      label: 'My Events',
      icon: ['far', 'calendar'],
      access: isSuperuser || isZeditor,
    },
    {
      url: urls.editorRecentEdits(userId),
      label: 'Recent Edits',
      icon: ['far', 'pen-to-square'],
      access: isSuperuser || isZeditor,
    },
    {
      url: urls.editorProfile(userId),
      label: 'Editor Profile',
      icon: ['far', 'id-card'],
      access: isSuperuser || isZeditor,
    },
    {
      url: urls.editorTimesheets,
      label: 'Timesheets',
      icon: 'business-time',
      access: isSuperuser || isZeditor,
    },

    // -----------------------------------
    {
      type: 'separator',
      label: 'Reports',
      access: isSuperuser,
    },
    {
      url: urls.editorReport,
      label: 'Top Editors',
      icon: 'user-pen',
      access: isSuperuser,
    },
    {
      url: urls.editsReport,
      label: 'Recent Edits',
      icon: ['far', 'pen-to-square'],
      access: isSuperuser,
    },
    {
      url: urls.eventQAReport,
      label: 'Event QA',
      icon: ['far', 'calendar-lines-pen'],
      access: isSuperuser,
    },
    {
      url: urls.poiQAReport,
      label: 'POI QA',
      icon: 'location-check',
      access: isSuperuser,
    },
    {
      url: urls.poiDuplicateReport,
      label: 'POI Duplicates',
      icon: ['far', 'building-circle-exclamation'],
      access: isSuperuser,
    },
    {
      url: urls.userReport,
      label: 'Users',
      icon: 'users',
      access: isSuperuser,
    },

    // -----------------------------------
    {
      type: 'separator',
      label: 'Admin',
      access: isSuperuser || isStaff,
    },
    {
      url: urls.djangoAdmin,
      label: 'Django Admin',
      icon: 'cog',
      access: isStaff,
      targetBlank: true,
    },
    {
      url: urls.wagtailAdmin,
      label: 'Wagtail CMS',
      icon: 'cog',
      access: isStaff,
      targetBlank: true,
    },
    {
      url: urls.producerRequests,
      label: 'Producer Requests',
      icon: 'list-check',
      access: isSuperuser,
    },

    // -----------------------------------
    {
      type: 'separator',
      access: true,
    },
    {
      url: logoutUrl,
      label: 'Log Out',
      icon: ['far', 'sign-out'],
      access: true,
    },
  ].filter(item => item.access);

  return (
    <>
      {isAuthenticated ? (
        <DropdownButton
          text={userDataFetching ? <LoadingBubbles /> : labelText}
          menuAnchor="right"
          appendIcon="bars"
        >
          <div className="link-list py-4" style={{ width: 280 }}>
            {links.map(({ url, label, icon, type = 'link', targetBlank }) => {
              const linkAttrs = (widgetId || targetBlank) ? {
                target: '_blank',
                rel: 'noopener noreferrer',
              } : {};

              return type === 'separator' ? (
                <div key={label + type} className={classNames('link-list-separator', 'px-4', !!label && 'has-label')}>{label}</div>
              ) : (
                <a key={label + url} href={url} className="link-list-item px-5" {...linkAttrs}>
                  <Icon i={icon} />
                  <span>{label}</span>
                </a>
              );
            })}
          </div>
        </DropdownButton>
      ) : (
        <a className="btn-z sm clear" href="#log_in" onClick={handleLoginClick}>Log In</a>
      )}
      <LoginView
        modal={true}
        isOpen={loginModalIsOpen}
        defaultToRegister={isRegisterCTA}
        showPromoWrapper={isRegisterCTA}
        onRequestClose={handleLoginModalClose}
        isSocialAuthEnabled={isSocialAuthEnabled}
      />
    </>
  );
};

UserMenu.propTypes = {
  initialIsAuthenticated: PropTypes.bool, // Set true if the user is known to be already signed in when the component mounts
  isSocialAuthEnabled: PropTypes.bool,
};

export default UserMenu;
